import React, { useRef, useState } from 'react';

import OpenerFormatNote from '@/components/admin/PrintTextComparison/OpenerFormatNote';
import SectionChanges from '@/components/admin/PrintTextComparison/SectionChanges';
import SectionEditLink from '@/components/admin/PrintTextComparison/SectionEditLink';
import SectionPreviewLink from '@/components/admin/PrintTextComparison/SectionPreviewLink';
import { AnalyzedSection, Change, EditHref } from '@/components/admin/PrintTextComparison/types';
import VerdictBadge from '@/components/admin/PrintTextComparison/VerdictBadge';

import { highlightChange } from './locate';
import PourWorkspace from './PourWorkspace';
import SectionList from './SectionList';

type Layout = 'split' | 'tabs';

const CHANGED_LABEL = 'tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-spa-secondary';
const CHANGED_PANEL = 'tw-mb-4 tw-box-border tw-rounded tw-border tw-border-solid tw-border-spa-border '
  + 'tw-bg-spa-bg-secondary tw-p-3';

const matchLabel = (ratio: number | null) => {
  if (ratio === null) return null;
  return ratio >= 0.9999 ? '100% match' : `${(ratio * 100).toFixed(1)}% match`;
};

// Land on the first section that actually changed (the actionable one); otherwise the first
// section. A null sectionId is a valid selection (the synthetic not-online-yet row) — undefined
// means nothing to select.
const initialSelection = (sections: AnalyzedSection[]) => {
  const firstChanged = sections.find(s => s.status === 'changed');
  return (firstChanged || sections[0])?.sectionId;
};

interface DetailProps {
  editHref: EditHref;
  layout: Layout;
  onSectionSaved: (sectionId: number, html: string) => void;
  previewHref: EditHref;
  refreshing: boolean;
  section: AnalyzedSection;
}

// The comparison verdict + inline diff sit above the generated ⟷ current editors, so what the
// comparison found is visible right where you'd fix it. Clicking a change scrolls both editors to
// it and highlights it there (draft pane gets the print text, current pane the online text). The
// highlight stays until another change is clicked.
const PourDetail = ({ editHref, layout, onSectionSaved, previewHref, refreshing, section }: DetailProps) => {
  const match = matchLabel(section.ratio);
  const href = editHref(section.sectionId);
  const preview = previewHref(section.sectionId);
  const editorsRef = useRef<HTMLDivElement>(null);

  const locateChange = (change: Change) => {
    const editor = (pane: string) => editorsRef.current
      ?.querySelector<HTMLElement>(`[data-pour-pane="${pane}"] .fr-element`) ?? null;
    const context = { after: change.contextAfter, before: change.contextBefore };
    // Current-online last: it's the preferred scroll target (draft is the fallback, e.g. for
    // print-only changes that have no online text).
    highlightChange([
      { ...context, phrase: change.printText, root: editor('draft') },
      { ...context, phrase: change.onlineText, root: editor('current') },
    ]);
  };

  return (
    <div>
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <h3 className="tw-m-0 tw-text-xl tw-font-semibold tw-text-spa-heading">{section.title}</h3>
        <VerdictBadge status={section.status} />
        {match && <p className="tw-m-0 tw-text-sm tw-tabular-nums tw-text-spa-secondary">{match}</p>}
        <OpenerFormatNote format={section.openerFormat} />
        {href && <SectionEditLink href={href} />}
        {preview && <SectionPreviewLink href={preview} />}
        {refreshing && <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">Refreshing comparison…</p>}
      </div>

      {section.changes.length > 0 && (
        <div className={CHANGED_PANEL}>
          <p className={CHANGED_LABEL}>What changed</p>
          <SectionChanges
            captionEditHref={href}
            changes={section.changes}
            vocabEditHref={editHref}
            onLocate={locateChange}
          />
        </div>
      )}

      <div ref={editorsRef}>
        <PourWorkspace
          key={section.sectionId ?? 'opener'}
          layout={layout}
          section={section}
          onSectionSaved={onSectionSaved}
        />
      </div>
    </div>
  );
};

interface Props {
  editHref: EditHref;
  layout: Layout;
  onSectionSaved: (sectionId: number, html: string) => void;
  previewHref: EditHref;
  refreshing: boolean;
  sections: AnalyzedSection[];
}

const PourView = ({ editHref, layout, onSectionSaved, previewHref, refreshing, sections }: Props) => {
  const [selectedId, setSelectedId] = useState<number | null | undefined>(() => initialSelection(sections));

  const selected = selectedId === undefined ? null : sections.find(s => s.sectionId === selectedId) || null;

  return (
    <div className="tw-mt-6 tw-flex tw-gap-6">
      <SectionList sections={sections} selectedId={selectedId} onSelect={setSelectedId} />
      <div className="tw-min-w-0 tw-flex-1">
        {selected ? (
          <PourDetail
            editHref={editHref}
            layout={layout}
            previewHref={previewHref}
            refreshing={refreshing}
            section={selected}
            onSectionSaved={onSectionSaved}
          />
        ) : (
          <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">Select a section to preview its pour.</p>
        )}
      </div>
    </div>
  );
};

export default PourView;
